import { Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { get } from 'lodash';
import React, { useState } from 'react';
import * as yup from 'yup';
import { updateMedicationList, validateExistingMedicationListName } from '../../Async/MedicationListsAsync';
import { PermissionsCheck } from '../../Components/Auth/PermissionsCheck';
import { Button, ButtonMode, ButtonVariant } from '../../Components/Button';
import { StyledDialog } from '../../Components/Dialog/StyledDialog';
import { StyledDialogButton } from '../../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../../Components/Dialog/StyledDialogContent';
import { getErrorIfTouchedFromObject } from '../../Components/FormikFields/getErrorText';
import { Header } from '../../Components/Header/Header';
import { CloseIcon, MedicationListIcon } from '../../Components/Icons';
import { NavMenu } from '../../Components/NavMenu/NavMenu';
import { StaticNavMenuItem } from '../../Components/NavMenu/StaticNavMenu';
import { StyledNavigationPrompt } from '../../Components/Prompts/StyledNavigationPrompt';
import { displayMessage } from '../../Config/ApplicationConfig';
import { useMedicationSettings } from '../../Config/MedicationConfig';
import { EditMedicationList } from '../../Contracts/EditMedicationList.Request';
import { MedicationListResponse } from '../../Contracts/MedicationList.Response';
import { ValidationResult } from '../../Contracts/ValidationResult.Response';
import { MedicationLocale, requiredField } from '../../Medications.en';
import { MedicationsPermissions } from '../../MedicationsPermissions';
import { debouncePromise } from '../../Shared/debouncePromise';
import {
    convertValidationResultToErrorsObjectV2,
    isDomainValidationError
} from '../../Shared/DomainValidationFunctions';
import { white50 } from '../../Styles/Colours';
import {
    createMedicationListStepFields,
    MedicationInformationStep,
    MedicationListFormErrors_TypeMissing,
    MedicationListInformationStep
} from '../MedicationListSharedSteps/MedicationListInformationStep';

const debouncedValidateExistingMedicationListName = debouncePromise<ValidationResult>(
    validateExistingMedicationListName,
    250
);

enum NavLinks {
    Details = 'details'
}

type EditMedicationListFormState = {
    id: string;
} & MedicationInformationStep;

const createMedicationListSchema = yup
    .object<EditMedicationListFormState>({
        id: yup.string(),
        name: yup
            .string()
            .required(requiredField)
            .test({
                name: 'Name must be unique',
                message: 'Duplicate medication name exists',
                test: async function validateName(this: yup.TestContext, value: string) {
                    const parent: EditMedicationListFormState = this.parent;
                    if (!value) return true;
                    const result = await debouncedValidateExistingMedicationListName(value, parent.id);
                    return result.success;
                }
            }),
        isScopeOfPracticeList: yup.bool().required(requiredField),
        isFormulary: yup.bool().required(requiredField),
        isDefaultList: yup.bool().required(requiredField)
    })
    .test({
        name: 'One type of list must be chosen',
        message: 'One type of list must be chosen',
        test(this: yup.TestContext, value: EditMedicationListFormState) {
            if (value.isDefaultList) return true;
            if (value.isScopeOfPracticeList) return true;
            if (value.isFormulary) return true;
            return this.createError({
                message: 'Medication list type required.',
                path: MedicationListFormErrors_TypeMissing
            });
        }
    });

const useStyles = makeStyles({
    header: {
        marginBottom: 24
    }
});

const getMedListStepErrors = (
    step: NavLinks,
    errors: FormikErrors<EditMedicationListFormState>,
    touchedFields?: FormikTouched<EditMedicationListFormState>
): number => {
    switch (step) {
        case NavLinks.Details: {
            let map = createMedicationListStepFields.flatMap(fieldName =>
                getErrorIfTouchedFromObject(fieldName, errors, touchedFields)
            );
            map = [...map, get(errors, MedicationListFormErrors_TypeMissing, '')];

            return map.filter(it => !!it).length;
        }
    }

    return 0;
};

export const EditMedicationListDialog: React.FC<{
    medicationList: MedicationListResponse;
    onClose(): void;
    onComplete(): void;
}> = props => {
    const styles = useStyles(props);

    const [activeLink, setActiveLink] = useState(NavLinks.Details);

    const medicationSettings = useMedicationSettings();

    return (
        <Formik<EditMedicationListFormState>
            initialValues={{
                id: props.medicationList.id,
                name: props.medicationList.name,
                isScopeOfPracticeList: props.medicationList.isScopeOfPracticeList,
                isDefaultList: props.medicationList.isDefaultList,
                isFormulary: props.medicationList.isFormulary
            }}
            validationSchema={createMedicationListSchema}
            onSubmit={async (values, formikActions) => {
                const payload: EditMedicationList = {
                    ...values,
                    authoringSystemForeignKey: props.medicationList.authoringSystemForeignKey,
                    authoringSystem: props.medicationList.authoringSystem
                };

                try {
                    await updateMedicationList(props.medicationList.id, payload);
                    formikActions.resetForm({ values });
                    displayMessage('Medication list updated');
                    props.onComplete();
                } catch (err) {
                    if (isDomainValidationError(err)) {
                        const errors = convertValidationResultToErrorsObjectV2(err.content, MedicationLocale);

                        formikActions.setErrors(errors.fieldErrors);
                    }
                }
            }}
        >
            {(formProps: FormikProps<EditMedicationListFormState>) => {
                return (
                    <StyledDialog
                        open
                        size='lg'
                        modal
                        testId='edit-medication-list'
                        title={'Edit medication list'}
                        sidebarRender={({ collapsed }) => {
                            return (
                                <NavMenu isCollapsed={collapsed}>
                                    <StaticNavMenuItem
                                        isCollapsed={collapsed}
                                        errors={getMedListStepErrors(
                                            NavLinks.Details,
                                            formProps.errors,
                                            formProps.touched
                                        )}
                                        icon={<MedicationListIcon size='24' />}
                                        onClick={() => {
                                            setActiveLink(NavLinks.Details);
                                        }}
                                        title={'Medication list details'}
                                        isActive={activeLink === NavLinks.Details}
                                        testId={'medication-list-details'}
                                    />
                                </NavMenu>
                            );
                        }}
                        onRequestClose={props.onClose}
                        titleBarRight={
                            <>
                                <Tooltip title='Close'>
                                    <Button
                                        iconOnly
                                        onClick={props.onClose}
                                        noShadowOnHover
                                        subtleShadeOnHover
                                        testId={`navigation-control-close`}
                                    >
                                        <CloseIcon size='20' color={white50} noPadding />
                                    </Button>
                                </Tooltip>
                            </>
                        }
                        actions={
                            <>
                                <StyledDialogButton
                                    mode={ButtonMode.solid}
                                    variant={ButtonVariant.light}
                                    width={161}
                                    key='close'
                                    onClick={props.onClose}
                                    testId={`navigation-control-close`}
                                >
                                    Cancel
                                </StyledDialogButton>
                                <PermissionsCheck key='save' permission={MedicationsPermissions.WriteMedicationLists}>
                                    <StyledDialogButton
                                        disabled={formProps.isSubmitting || formProps.isValidating}
                                        width={161}
                                        testId={'navigation-control-save'}
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();

                                            formProps.submitForm();
                                        }}
                                    >
                                        Save
                                    </StyledDialogButton>
                                </PermissionsCheck>
                            </>
                        }
                    >
                        <div className={styles.header}>
                            <Header
                                title='Medication list details'
                                updatedOn={props.medicationList.lastUpdatedOn}
                                updatedBy={props.medicationList.lastUpdatedBy}
                                createdOn={props.medicationList.createdOn}
                            />
                        </div>
                        <div>{activeLink === NavLinks.Details && <MedicationListInformationStep verbalOrderTerm={medicationSettings.settings.verbalOrderTerm}/>}</div>
                        <StyledNavigationPrompt when={formProps.dirty} onSave={formProps.submitForm} />
                    </StyledDialog>
                );
            }}
        </Formik>
    );
};
